import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { Modal } from "@/components/Modal";
import { ProviderGlyph } from "@/components/ProviderGlyph";
import { RowSkeleton } from "@/components/Skeleton";
import { useToast } from "@/components/Toast";
import type { RealKey } from "@/types";

export function RealKeys() {
  const [rows, setRows] = useState<RealKey[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const toast = useToast();

  async function load() {
    try {
      setRows(await api<RealKey[]>("/api/admin/real-keys"));
    } catch (e: any) {
      setErr(e?.message ?? "load failed");
    }
  }
  useEffect(() => {
    load();
  }, []);

  // Open the create modal if arrived via command-palette deep link (?new=1)
  useEffect(() => {
    if (params.get("new") === "1") {
      setCreateOpen(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  // `n` keyboard shortcut to create new
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const isForm =
        t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if (!isForm && e.key === "n" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setCreateOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filtered = rows
    ? rows.filter((r) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return (
          r.label.toLowerCase().includes(q) ||
          r.provider.toLowerCase().includes(q) ||
          r.upstream_base_url.toLowerCase().includes(q) ||
          (r.notes ?? "").toLowerCase().includes(q)
        );
      })
    : null;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl display">Real keys</h1>
          <p className="text-sm text-slate-400">
            Actual upstream provider credentials. Encrypted at rest with AES-GCM; never leave this server.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setCreateOpen(true)}>
          + Add real key
          <kbd className="ml-1 text-[10px] opacity-60">n</kbd>
        </button>
      </header>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <input
            type="search"
            className="input pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by label, provider, upstream…"
            aria-label="Search real keys"
          />
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">⌕</span>
          <kbd className="hidden sm:inline-block absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-600 mono">
            /
          </kbd>
        </div>
        <div className="text-xs text-slate-500">
          {rows === null ? "…" : `${filtered?.length ?? 0} of ${rows.length}`}
        </div>
      </div>

      {err && <div className="card text-rose-300 text-sm">{err}</div>}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500 uppercase tracking-wide bg-white/5">
            <tr>
              <th className="py-3 px-4">Provider</th>
              <th>Label</th>
              <th>Upstream</th>
              <th>Auth</th>
              <th>Rotated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <>
                <RowSkeleton cols={6} />
                <RowSkeleton cols={6} />
                <RowSkeleton cols={6} />
              </>
            )}
            {rows && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-10 px-6 text-center text-slate-400">
                  <div className="text-lg mb-1">No real keys yet</div>
                  <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
                    Add your first upstream credential (Anthropic API key, OpenAI token, Finnhub
                    token, Twilio auth, etc.). It's encrypted immediately and never leaves this
                    server.
                  </p>
                  <button className="btn-primary" onClick={() => setCreateOpen(true)}>
                    Add real key
                  </button>
                </td>
              </tr>
            )}
            {rows && rows.length > 0 && filtered?.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 px-6 text-center text-slate-500 text-sm">
                  No matches for "{query}".
                </td>
              </tr>
            )}
            {filtered?.map((r) => {
              const days = Math.floor(
                (Date.now() - new Date(r.rotated_at).getTime()) / 86400000
              );
              const edge =
                days >= 180 ? "row-edge-err" : days >= 90 ? "row-edge-warn" : "row-edge-ok";
              return (
                <tr
                  key={r.id}
                  className={`border-t border-white/5 hover:bg-white/[0.03] transition-colors ${edge}`}
                >
                  <td className="py-3 px-4">
                    <ProviderGlyph provider={r.provider} />
                  </td>
                  <td className="font-medium">
                    <Link to={`/real-keys/${r.id}`} className="hover:text-accent-500">
                      {r.label}
                    </Link>
                  </td>
                  <td className="mono text-xs text-slate-500 truncate max-w-[260px]">
                    {r.upstream_base_url}
                  </td>
                  <td className="text-slate-400 text-xs">
                    {r.auth_scheme}
                    {r.auth_header_name ? (
                      <span className="text-slate-500 mono"> · {r.auth_header_name}</span>
                    ) : null}
                  </td>
                  <td className="text-slate-400 whitespace-nowrap">
                    {days === 0 ? "today" : `${days}d ago`}
                  </td>
                  <td className="text-right pr-4">
                    <Link
                      className="text-accent-500 hover:underline text-xs"
                      to={`/real-keys/${r.id}`}
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={createOpen} title="Add real key" onClose={() => setCreateOpen(false)}>
        <RealKeyForm
          onCreated={async () => {
            setCreateOpen(false);
            toast.success("Real key saved");
            await load();
          }}
        />
      </Modal>
    </div>
  );
}

export function RealKeyForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({
    label: "",
    provider: "",
    upstream_base_url: "",
    auth_scheme: "header" as "header" | "bearer" | "basic" | "query",
    auth_header_name: "",
    auth_username: "",
    value: "",
    notes: "",
    issuer_email: "",
    quota_amount: "",
    quota_used: "",
    quota_unit: "",
    quota_period: "" as "" | "daily" | "monthly" | "lifetime",
    // Audit 2026-05-21 P1-18: budget cap MUST be set at create time, not
    // post-hoc via PATCH (operators forget; provisioning scripts already
    // do this). Empty string = no cap, matches the BE Decimal | None contract.
    monthly_budget_usd: "",
  });
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function upd<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const payload: any = {
        label: form.label,
        provider: form.provider,
        upstream_base_url: form.upstream_base_url,
        auth_scheme: form.auth_scheme,
        value: form.value,
        notes: form.notes || null,
      };
      if (form.auth_scheme === "header" || form.auth_scheme === "query") {
        payload.auth_header_name = form.auth_header_name || null;
      }
      if (form.auth_scheme === "basic") {
        payload.auth_username = form.auth_username || null;
      }
      if (form.issuer_email.trim()) payload.issuer_email = form.issuer_email.trim();
      if (form.quota_amount.trim()) payload.quota_amount = form.quota_amount.trim();
      if (form.quota_used.trim()) payload.quota_used = form.quota_used.trim();
      if (form.quota_unit.trim()) payload.quota_unit = form.quota_unit.trim();
      if (form.quota_period) payload.quota_period = form.quota_period;
      if (form.monthly_budget_usd.trim())
        payload.monthly_budget_usd = form.monthly_budget_usd.trim();
      await api("/api/admin/real-keys", { method: "POST", json: payload });
      onCreated();
    } catch (e: any) {
      setErr(e?.message ?? "create failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Label">
          <input
            className="input"
            value={form.label}
            onChange={(e) => upd("label", e.target.value)}
            placeholder="Anthropic Prod"
            required
          />
        </Field>
        <Field label="Provider">
          <input
            className="input"
            value={form.provider}
            onChange={(e) => upd("provider", e.target.value)}
            placeholder="anthropic"
            required
          />
        </Field>
      </div>
      <Field label="Upstream base URL">
        <input
          className="input mono"
          value={form.upstream_base_url}
          onChange={(e) => upd("upstream_base_url", e.target.value)}
          placeholder="https://api.anthropic.com"
          required
        />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Auth scheme">
          <select
            className="input"
            value={form.auth_scheme}
            onChange={(e) => upd("auth_scheme", e.target.value as any)}
          >
            <option value="header">header (custom name)</option>
            <option value="bearer">Authorization: Bearer</option>
            <option value="basic">Authorization: Basic</option>
            <option value="query">query string</option>
          </select>
        </Field>
        {(form.auth_scheme === "header" || form.auth_scheme === "query") && (
          <Field label="Auth key name">
            <input
              className="input mono"
              value={form.auth_header_name}
              onChange={(e) => upd("auth_header_name", e.target.value)}
              placeholder={form.auth_scheme === "header" ? "x-api-key" : "api_key"}
              required
            />
          </Field>
        )}
        {form.auth_scheme === "basic" && (
          <Field label="Basic username (e.g. Twilio SID)">
            <input
              className="input mono"
              value={form.auth_username}
              onChange={(e) => upd("auth_username", e.target.value)}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxx"
            />
          </Field>
        )}
      </div>
      <Field label="Secret value">
        <input
          className="input mono"
          type="password"
          value={form.value}
          onChange={(e) => upd("value", e.target.value)}
          autoComplete="new-password"
          required
        />
      </Field>
      <Field label="Notes (optional)">
        <textarea
          className="input"
          rows={2}
          value={form.notes}
          onChange={(e) => upd("notes", e.target.value)}
        />
      </Field>

      <fieldset className="border border-white/10 rounded-lg p-4 space-y-3">
        <legend className="px-2 text-xs uppercase tracking-wider text-slate-400">
          Provenance &amp; Quota (optional)
        </legend>
        <Field label="Issuer email / account">
          <input
            className="input mono"
            value={form.issuer_email}
            onChange={(e) => upd("issuer_email", e.target.value)}
            placeholder="naman@smarttouch.ai"
          />
        </Field>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Quota amount">
            <input
              className="input mono"
              type="number"
              min="0"
              step="0.01"
              value={form.quota_amount}
              onChange={(e) => upd("quota_amount", e.target.value)}
              placeholder="100.00"
            />
          </Field>
          <Field label="Used so far">
            <input
              className="input mono"
              type="number"
              min="0"
              step="0.01"
              value={form.quota_used}
              onChange={(e) => upd("quota_used", e.target.value)}
              placeholder="0.00"
            />
          </Field>
          <Field label="Unit">
            <input
              className="input mono"
              value={form.quota_unit}
              onChange={(e) => upd("quota_unit", e.target.value)}
              placeholder="USD"
              maxLength={16}
            />
          </Field>
        </div>
        <Field label="Quota period">
          <select
            className="input"
            value={form.quota_period}
            onChange={(e) => upd("quota_period", e.target.value as any)}
          >
            <option value="">— none —</option>
            <option value="daily">daily</option>
            <option value="monthly">monthly</option>
            <option value="lifetime">lifetime</option>
          </select>
        </Field>
        <Field label="Gatekeeper monthly budget cap (USD)">
          <input
            className="input mono"
            type="number"
            min="0"
            step="0.01"
            value={form.monthly_budget_usd}
            onChange={(e) => upd("monthly_budget_usd", e.target.value)}
            placeholder="e.g. 100.00 — leave empty for no cap"
          />
          <div className="text-xs text-slate-500 mt-1">
            Independent of any upstream provider spend cap. When monthly
            spend crosses this value, consumers get 429 (code=
            <code>gk_budget_exhausted</code>).
          </div>
        </Field>
      </fieldset>

      {err && <div className="text-rose-300 text-xs">{err}</div>}
      <div className="flex justify-end">
        <button className="btn-primary" disabled={loading} type="submit">
          {loading ? "Saving…" : "Save real key"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      {children}
    </label>
  );
}
